import { ActionRecord, ActionReference } from "@/shared/domain/actions";
import { RobotoEndpoint } from "@/types";

export function isActionReference(
  record: ActionRecord | ActionReference | null,
): record is ActionReference {
  return (
    record !== null && Object.prototype.hasOwnProperty.call(record, "owner")
  );
}

/**
 * The org that owns an action, whichever way the record at hand spells it: an
 * `ActionReference` carries `owner`, while an `ActionRecord` and an invocation's
 * `provenance.action` carry `org_id`. Empty and null both read as unknown, so callers get
 * `undefined` rather than a falsy string they have to re-check.
 */
function actionOwnerOrgId(
  action: ActionRecord | ActionReference,
): string | undefined {
  const owner = isActionReference(action) ? action.owner : action.org_id;
  return owner ? owner : undefined;
}

/**
 * The action's owning org when that is someone other than `viewerOrgId` — the org the page
 * doing the asking is being drawn for — and `undefined` when the action is the viewer's own or
 * its owner is unknown.
 *
 * Every Action Hub action answers here, since the hub publishes out of orgs such as
 * `roboto-public` (or `roboto-team` on a private stack) that the viewer is not a member of. So
 * does any other org's action the viewer can see. Passing `undefined` for `viewerOrgId` — a
 * session that has not resolved an org yet — reports the owner rather than assuming the
 * action is the viewer's, because {@link actionDetailPath} can carry an owner it did not need
 * but cannot recover one it left out.
 */
export function ownerOrgIdIfNotViewers(
  action: ActionRecord | ActionReference,
  viewerOrgId: string | undefined,
): string | undefined {
  const owner = actionOwnerOrgId(action);
  return owner !== undefined && owner !== viewerOrgId ? owner : undefined;
}

/**
 * Path to an action's detail page, as the `/actions/:actionRef/*` route reads it: one segment
 * is a bare name, two are the owner then the name.
 *
 * The owner segment is what the detail page passes back as the resource owner it fetches with,
 * so a bare name resolves against the viewer's own org and the service answers 404 for any
 * action that org does not own. An Action Hub action is owned by the org that published it, so
 * linking to it by bare name reaches nothing.
 */
export function actionDetailPath(
  action: ActionRecord | ActionReference,
  viewerOrgId: string | undefined,
): string {
  const owner = ownerOrgIdIfNotViewers(action, viewerOrgId);

  return owner !== undefined
    ? `/actions/${owner}/${action.name}`
    : `/actions/${action.name}`;
}

export const datasetFilesQueryEndpoint: RobotoEndpoint = (pathParams) => {
  const datasetId = pathParams?.datasetId;

  return `/datasets/${datasetId}/files/query`;
};
